// Launch day: the rollout dashboard, sized for a 4K TV across the room.
//
// Two ways in, one component:
// - Signed-in employees open /launch inside the app shell like any other page.
// - The TV computer (no Google sign-in) boots through the kiosk path in App.tsx,
//   holding a remote-view token redeemed from a one-time code (lib/launch.ts),
//   and renders this page standalone with `session` set.
//
// The payload is deliberately aggregate-only (no names, emails, or project
// titles): it renders on a shared office screen. Realtime-first: hero tiles and
// the primary chart row are the LAST HOUR (minute grain); 24h context rides in
// sublines and a secondary row. Data re-reads on a fixed interval
// (LAUNCH_POLL_MS, matching the worker's fifteen-minute snapshot refresh; the
// route itself is a pure cache read) — the console's one documented exception
// to the no-polling rule (see launchDashboardQuery).
//
// That interval stops the moment the CREDENTIAL behind it dies, on either path
// in: the kiosk retires its remote-view token on the expiry clock it already
// knows, and any auth rejection (401 or 403) switches the timer off rather than
// re-asking a question only a human can answer. An unattended screen used to
// keep asking forever, which is how an expired session put 13,460 failed
// requests, 5.4% of all traffic, through /launch unnoticed for 13 hours.
//
// Visual system (dataviz house rules): color follows the ENTITY via the
// validated `--ma-chart-*` tokens (globals.css theme blocks) — messages are
// always blue, signups always green, and so on, across tiles, sparklines, and
// both chart rows; errors wear the reserved danger status color. Values and
// labels wear TEXT tokens, never the series color — the colored mark beside
// them carries identity. Charts are hand-built SVG (no chart library in the
// repo): a gradient area+line for the minute grain, rounded bars for the hour
// grain, arc gauges for the rate lane.
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ExperimentFilter } from "../components/experiment-filter";
import type { ExperimentScope } from "../lib/experiment-scope";
import {
  BarChart,
  Button,
  type ChartSeries,
  DualGauge,
  Gauge,
  IconButton,
  LineAreaChart,
  Spinner,
  StatTile,
} from "@recruiter/shared";
import type {
  LaunchDashboardResponse,
  LaunchLiveFunnel,
  LaunchRemoteLinkResponse,
} from "@recruiter/shared/types";

import { ApiError, mintLaunchRemoteLink } from "../lib/api";
import {
  clearLaunchSession,
  formatLaunchCode,
  type LaunchSession,
  themeFromSearch,
} from "../lib/launch";
import { canWrite } from "../lib/areas";
import {
  isAuthFailure,
  LAUNCH_POLL_MS,
  launchArmQuery,
  launchDashboardQuery,
  myAreasQuery,
} from "../lib/queries";
import { formatStatValue, formatUsdTotal } from "../lib/format";
import { applyTheme, useTheme } from "../lib/theme-client";
import { ErrorState, LoadingState } from "../components/states";
import { MoonIcon, SunIcon } from "../components/icons";
import { type LaunchDrill, LaunchDrilldownModal } from "../components/launch-drilldown";
import launchArt from "../assets/launch-day.png";
import "./launch.css";

/** The entity → color-token contract. One place, so a tile and its charts can
 *  never disagree on a hue. */
type SeriesKey =
  | "messages"
  | "signups"
  | "searches"
  | "offers"
  | "picks"
  | "projects"
  | "selfsearch"
  | "errors"
  | "frustration"
  | "replies";
const SERIES_COLOR: Record<SeriesKey, string> = {
  messages: "var(--ma-chart-messages)",
  signups: "var(--ma-chart-signups)",
  searches: "var(--ma-chart-searches)",
  offers: "var(--ma-chart-offers)",
  picks: "var(--ma-chart-picks)",
  projects: "var(--ma-chart-projects)",
  selfsearch: "var(--ma-chart-selfsearch)",
  errors: "var(--rc-color-danger)",
  // The one entity that deliberately shares a hue with another. Frustration is
  // not a categorical slot in the --ma-chart-* set; it is an adverse STATE, and
  // the status color is what its own tile already wears (.lp-tile--danger sets
  // exactly this accent). Sharing red with errors is safe here because no plot
  // on this board carries both: each is a single-series figure with its own
  // title and swatch, so identity never rests on color alone.
  frustration: "var(--rc-color-danger)",
  replies: "var(--ma-chart-replies)",
};

/** SERIES_COLOR as a lookup the shared chart primitives can read (they take a
 *  color Map, not this page's own SeriesKey union). Built once — the fixed
 *  entity vocabulary above never changes at runtime, unlike a BI chart's
 *  dashboard-defined series list. */
const seriesColors = new Map<string, string>(Object.entries(SERIES_COLOR));

/** One entity's series as the shared chart primitives' ChartSeries shape. */
function chartSeries(key: SeriesKey, label: string, values: number[]): ChartSeries {
  return { key, label, values };
}

// --- Small pieces -----------------------------------------------------------

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // A per-second wall clock so the room can trust the screen is live. Local
    // UI state only — not a data poll.
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <time className="lp-clock" dateTime={now.toISOString()}>
      {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
    </time>
  );
}

// --- Remote link modal ------------------------------------------------------

function RemoteLinkModal({ onClose }: { onClose: () => void }) {
  const mint = useMutation<LaunchRemoteLinkResponse, unknown>({
    mutationFn: mintLaunchRemoteLink,
  });
  const fired = useRef(false);
  useEffect(() => {
    // Mint exactly once per open, not per render.
    if (!fired.current) {
      fired.current = true;
      mint.mutate();
    }
  }, [mint]);

  const kioskUrl = `${window.location.origin}/launch`;

  return (
    <div className="lp-modal" role="dialog" aria-modal="true" aria-label="Open in remote">
      <div className="lp-modal__panel">
        <h2 className="lp-modal__title">Open on the TV</h2>
        {mint.isPending ? <LoadingState label="Minting a code…" /> : null}
        {mint.isError ? <ErrorState error={mint.error} onRetry={() => mint.mutate()} /> : null}
        {mint.isSuccess ? (
          <>
            <ol className="lp-modal__steps">
              <li>
                On the TV computer, open <strong className="lp-modal__url">{kioskUrl}</strong>
              </li>
              <li>Type in this one-time code:</li>
            </ol>
            <p className="lp-modal__code" aria-label="One-time code">
              {formatLaunchCode(mint.data.code)}
            </p>
            <p className="lp-modal__hint">
              The code works once and expires at{" "}
              {new Date(mint.data.expiresAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
              . The TV then stays signed in for 12 hours.
            </p>
          </>
        ) : null}
        <div className="lp-modal__actions">
          {mint.isSuccess ? (
            <Button variant="outline" tone="neutral" onClick={() => mint.mutate()}>
              New code
            </Button>
          ) : null}
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}

// --- The dashboard ----------------------------------------------------------

function ratioDisplay(num: number, den: number): { ratio: number; display: string } {
  if (den <= 0) return { ratio: 0, display: "-" };
  const r = num / den;
  return { ratio: r, display: `${(r * 100).toFixed(r >= 0.1 ? 0 : 1)}%` };
}

// The live funnel's five lanes, in flow order. Each lane wears the series
// color of the tile it corresponds to, so the board reads as one system.
const LIVE_STEPS: ReadonlyArray<{
  key: LaunchLiveFunnel["clients"][number]["step"];
  label: string;
  series: SeriesKey;
}> = [
  { key: "arrived", label: "Arrived", series: "signups" },
  { key: "chatting", label: "Chatting", series: "messages" },
  { key: "searching", label: "Searching", series: "searches" },
  { key: "offers_in", label: "Offers in", series: "offers" },
  { key: "picked", label: "Picked", series: "picks" },
];

function liveRecency(secondsAgo: number): string {
  return secondsAgo < 60 ? "now" : `${Math.floor(secondsAgo / 60)}m`;
}

// Most chips one lane may show. Keeps the panel's height in the same league as
// the 4x2 health grid beside it, whatever the traffic.
const LANE_CHIP_MAX = 4;

function LiveFunnel({ live }: { live: LaunchLiveFunnel }) {
  const total = LIVE_STEPS.reduce((n, s) => n + (live.counts[s.key] ?? 0), 0);
  return (
    <div className="lp-live-funnel" aria-label="Currently in the funnel">
      <div className="lp-live-funnel__head">
        {/* Reuses the shared StatTile label typography (rc-stat-tile__label,
            loaded globally once StatTile is imported anywhere on this page) -
            this header isn't a tile itself, just borrowing its look. */}
        <span className="rc-stat-tile__label">Currently in the funnel</span>
        <span className="lp-live-funnel__total">
          {total} active, last {live.windowMinutes} min
        </span>
      </div>
      {total === 0 ? (
        <p className="lp-live-funnel__empty">
          Quiet right now. Nobody has written in the last {live.windowMinutes} minutes.
        </p>
      ) : (
        <div className="lp-live-funnel__lanes">
          {LIVE_STEPS.map((s) => {
            const count = live.counts[s.key] ?? 0;
            // Hard per-lane cap: with real traffic the backend's list is big
            // enough that rendering every chip grows this panel past the fold
            // and pushes the 24h charts off a TV nobody can scroll (prod,
            // 2026-08-03). The lane count is the truth; "+N" carries the rest.
            const chips = live.clients.filter((c) => c.step === s.key).slice(0, LANE_CHIP_MAX);
            const overflow = count - chips.length;
            return (
              <div
                key={s.key}
                className="lp-lane"
                style={{ "--lp-accent": SERIES_COLOR[s.series] } as CSSProperties}
              >
                <div className="lp-lane__head">
                  <span className="lp-lane__name">{s.label}</span>
                  <span className="lp-lane__count">{count}</span>
                </div>
                <div className="lp-lane__chips">
                  {chips.map((c) => (
                    <span
                      key={c.tag}
                      className={"lp-chip" + (c.secondsAgo < 60 ? " lp-chip--now" : "")}
                      title={`Active ${liveRecency(c.secondsAgo)} ago`}
                    >
                      <span className="lp-chip__dot" aria-hidden />
                      <span className="lp-chip__tag">{c.tag}</span>
                      <span className="lp-chip__time">{liveRecency(c.secondsAgo)}</span>
                    </span>
                  ))}
                  {overflow > 0 ? <span className="lp-chip lp-chip--more">+{overflow}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Dashboard({
  data,
  onDrill,
}: {
  data: LaunchDashboardResponse;
  /** Opens the drilldown modal for one metric. Absent in kiosk mode, where
   *  every number stays a plain tile: the remote-view token buys the aggregate
   *  read only, and the rows behind it carry names the TV must never show. */
  onDrill?: (drill: LaunchDrill) => void;
}) {
  const { now, sellers, agentHealth, attention, funnel, liveFunnel, hourly, minutely } = data;
  // A tile passes its metric + the window its headline number shows; the modal
  // seeds there and carries its own switcher for the tile's other windows.
  // Returns undefined without onDrill, which leaves the tile a plain div.
  const drill = (metric: LaunchDrill["metric"], window: LaunchDrill["window"], label: string) =>
    onDrill ? () => onDrill({ metric, window, label }) : undefined;

  const searchedRate = ratioDisplay(funnel.searchedUsers, funnel.engagedUsers);
  const pickedRate = ratioDisplay(funnel.pickedUsers, funnel.engagedUsers);
  const errorRate = ratioDisplay(agentHealth.errors1h, agentHealth.llmCalls1h);
  const errorColor =
    errorRate.ratio >= 0.05
      ? "var(--rc-color-danger)"
      : errorRate.ratio >= 0.02
        ? "var(--rc-color-warning)"
        : "var(--rc-color-success)";
  const errorWord =
    errorRate.ratio >= 0.05 ? "high" : errorRate.ratio >= 0.02 ? "elevated" : "healthy";
  // Concierge searches only: offers are born from concierge outreach, so
  // guest funnel searches in the denominator would just dilute the rate.
  const offerRate = ratioDisplay(now.offersToday, now.conciergeSearchesToday);
  // Distinct projects on both sides: per-seller picks over per-run searches
  // would mix units (one client picking 3 sellers reads as 3 conversions).
  const pickConversion = ratioDisplay(now.pickedProjectsToday, now.searchedProjectsToday);
  // Talent who came to the buyer product to pitch. These two are the ONLY rates
  // on the board whose denominator still counts them: everything else here has
  // already had them subtracted (see the `sellers` block in the payload), so
  // dividing by `projectsTotal` would be dividing by the leftovers.
  const sellerProjectRate = ratioDisplay(sellers.projects, sellers.projectsAll);
  const sellerUserRate = ratioDisplay(sellers.users, sellers.usersAll);

  return (
    <>
      {/* Rollout-first: the hero row IS the last hour; the day rides in the
          subline, and each tile carries its own minute-grain sparkline where
          one exists. */}
      <section className="lp-heroes" aria-label="Last hour headline numbers">
        <StatTile
          label="Signups, last hour"
          value={formatStatValue(now.newUsers1h)}
          sub={`${formatStatValue(now.newUsersToday)} today`}
          accent={SERIES_COLOR.signups}
          spark={minutely.newUsers}
          onOpen={drill("signups", "1h", "Signups")}
        />
        <StatTile
          label="Active users, last hour"
          value={formatStatValue(now.activeUsers1h)}
          sub={`${formatStatValue(now.activeUsers24h)} in 24h`}
          onOpen={drill("active_users", "1h", "Active users")}
        />
        <StatTile
          label="Client messages, last hour"
          value={formatStatValue(now.userMessages1h)}
          sub={`${formatStatValue(now.userMessages24h)} in 24h`}
          accent={SERIES_COLOR.messages}
          spark={minutely.userMessages}
          onOpen={drill("client_messages", "1h", "Client messages")}
        />
        <StatTile
          label="New projects, last hour"
          value={formatStatValue(now.newProjects1h)}
          sub={`${formatStatValue(now.newProjectsToday)} today`}
          accent={SERIES_COLOR.projects}
          spark={minutely.newProjects}
          onOpen={drill("new_projects", "1h", "New projects")}
        />
        <StatTile
          label="Searches, last hour"
          value={formatStatValue(now.searches1h)}
          sub={`${formatStatValue(now.searchesToday)} today`}
          accent={SERIES_COLOR.searches}
          spark={minutely.searchRuns}
          onOpen={drill("searches", "1h", "Searches")}
        />
        <StatTile
          label={`"Search myself" clicks, last hour`}
          value={formatStatValue(now.selfSearchClicks1h)}
          sub={`${formatStatValue(now.selfSearchClicksToday)} today`}
          accent={SERIES_COLOR.selfsearch}
          spark={minutely.selfSearchClicks}
          onOpen={drill("self_search_clicks", "1h", "Search myself clicks")}
        />
        <StatTile
          label="Offers, last hour"
          value={formatStatValue(now.offers1h)}
          sub={`${formatStatValue(now.offersToday)} today. Sent by sellers.`}
          accent={SERIES_COLOR.offers}
          spark={minutely.offers}
          onOpen={drill("offers", "1h", "Offers")}
        />
        {/* The board's only INBOUND-from-talent tile, and the one that answers
            "are talent replies still reaching us" during a deploy to the inbound
            flow. The headline counts every inbound message (the wire), and the
            sub-line names how many a human actually wrote, so the auto-responder
            share is visible rather than silently folded into the big number.
            No drilldown: `_DRILL_WINDOWS` has no seller-reply metric, and the
            row-level payload behind one would be talent conversation content,
            which the launch contract keeps off this board entirely. */}
        <StatTile
          label="Talent replies, last hour"
          value={formatStatValue(now.sellerReplies1h)}
          sub={`${formatStatValue(now.sellerRepliesToday)} today, ${formatStatValue(
            now.sellerRepliesGenuineToday,
          )} written by a person`}
          accent={SERIES_COLOR.replies}
          spark={minutely.sellerReplies}
        />
        <StatTile
          label="Talent picked, last hour"
          value={formatStatValue(now.handoffs1h)}
          sub={`${formatStatValue(now.handoffsToday)} today`}
          accent={SERIES_COLOR.picks}
          spark={minutely.picks}
          onOpen={drill("picks", "1h", "Talent picked")}
        />
      </section>

      {/* The hero tiles ARE the realtime charts (each carries its own minute
          sparkline), so there is no separate minute row repeating them. */}
      <section className="lp-pulse" aria-label="Conversion, rates, and attention">
        <DualGauge
          label="From first message"
          outerRatio={searchedRate.ratio}
          innerRatio={pickedRate.ratio}
          outerDisplay={searchedRate.display}
          innerDisplay={pickedRate.display}
          outerColor={SERIES_COLOR.searches}
          innerColor={SERIES_COLOR.picks}
          note={`Of ${formatStatValue(funnel.engagedUsers)} clients who wrote: ${searchedRate.display} searched, ${pickedRate.display} picked`}
        />
        <Gauge
          label="Error rate, last hour"
          ratio={errorRate.ratio / 0.1}
          display={errorRate.display}
          color={errorColor}
          note={`${formatStatValue(agentHealth.errors1h)} of ${formatStatValue(agentHealth.llmCalls1h)} calls, ${errorWord}`}
        />
        <Gauge
          label="Offers per concierge search, today"
          ratio={offerRate.ratio}
          display={offerRate.display}
          color={SERIES_COLOR.offers}
          note={`${formatStatValue(now.offersToday)} offers / ${formatStatValue(now.conciergeSearchesToday)} concierge searches`}
        />
        <Gauge
          label="Searches that led to a pick, today"
          ratio={pickConversion.ratio}
          display={pickConversion.display}
          color={SERIES_COLOR.picks}
          note={`${formatStatValue(now.pickedProjectsToday)} of ${formatStatValue(now.searchedProjectsToday)} searched projects picked talent`}
        />
        {/* How much of the day is talent in the buyer's seat, and therefore how
            much this board is NOT showing you anywhere else. Both arcs run to a
            25% full scale rather than 100%: the share is single digits when
            things are healthy, and a true-scale arc would be a hairline nobody
            reads across a room. The printed number is the honest one either way,
            same trick the error gauge uses. */}
        <Gauge
          label="Talent in the buyer seat, projects"
          ratio={sellerProjectRate.ratio / 0.25}
          display={sellerProjectRate.display}
          color="var(--rc-color-warning)"
          note={`${formatStatValue(sellers.projects)} of ${formatStatValue(sellers.projectsAll)} projects. Left out of every other number here.`}
        />
        <Gauge
          label="Talent in the buyer seat, people"
          ratio={sellerUserRate.ratio / 0.25}
          display={sellerUserRate.display}
          color="var(--rc-color-warning)"
          note={`${formatStatValue(sellers.users)} of ${formatStatValue(sellers.usersAll)} people signed up to sell, not to buy`}
        />
        {/* The one tile in this row that carries a sparkline, and it earns it:
            frustration is the board's only client-SENTIMENT signal, so the
            shape of the last hour (one bad minute, or a rising hour) is what
            the room actually wants off it. Its hue is the danger color the
            tile already wore. */}
        <StatTile
          label="Unsatisfied clients, last hour"
          value={formatStatValue(now.unsatisfiedUsers1h)}
          sub={`${formatStatValue(now.unsatisfiedUsersToday)} today. The signal that pages Slack.`}
          accent={SERIES_COLOR.frustration}
          spark={minutely.unsatisfied}
          danger={now.unsatisfiedUsers1h > 0}
          onOpen={drill("unsatisfied", "1h", "Unsatisfied clients")}
        />
      </section>

      {/* Left half: the two rows of health/total boxes. Right half: the live
          funnel — who is active right now, and how far they have gotten. */}
      <section className="lp-mid" aria-label="Agent health and live funnel">
        {/* Three rows of three, and each row is one KIND of number: agent health,
          then what needs an operator, then the running totals. It used to be two
          rows of four with health and attention sharing a row, which read as one
          undifferentiated wall of boxes across the room. */}
        <div className="lp-health" aria-label="Agent health, attention, and totals">
          <StatTile
            label="Turns, last hour"
            value={formatStatValue(agentHealth.turns1h)}
            sub={`${formatStatValue(agentHealth.turns24h)} in 24h`}
          />
          <StatTile
            label="LLM calls, last hour"
            value={formatStatValue(agentHealth.llmCalls1h)}
            sub={`${formatStatValue(agentHealth.llmCalls24h)} in 24h`}
          />
          <StatTile
            label="Errors, last hour"
            value={formatStatValue(agentHealth.errors1h)}
            sub={`${formatStatValue(agentHealth.errors24h)} in 24h`}
            danger={agentHealth.errors1h > 0}
            onOpen={drill("errors", "1h", "Agent errors")}
          />
          <StatTile
            label="Paused projects"
            value={formatStatValue(attention.pausedProjects)}
            danger={attention.pausedProjects > 0}
            onOpen={drill("paused", "all", "Paused projects")}
          />
          <StatTile
            label="Open flags"
            value={formatStatValue(attention.openFlags)}
            danger={attention.openFlags > 0}
            onOpen={drill("open_flags", "all", "Open flags")}
          />
          {/* Accounts an operator turned the assistant off for. On the board beside
            the other two attention counts because it is the same question they
            answer: how many people are not being served right now, and why. */}
          <StatTile
            label="Blocked users"
            value={formatStatValue(attention.blockedUsers)}
            sub="The assistant does not answer them"
            danger={attention.blockedUsers > 0}
            onOpen={drill("blocked", "all", "Blocked users")}
          />
          {/* Total spend, with the 24h burn as its sub. This tile sits in the
            running-totals row beside Users total and Projects total, and it
            reads like them: the 24h figure is a RATE and cannot answer what the
            launch has cost so far, which is what the room asks. It stays a
            third tile rather than a tenth one, because .lp-health is a fixed
            3-wide grid whose three rows ARE the three kinds of number. Custom
            markup (not <StatTile>, same as before the extraction): it shows two
            numbers, not one value + a plain subline. */}
          <div className="rc-stat-tile">
            <span className="rc-stat-tile__label">LLM cost, total</span>
            <span className="rc-stat-tile__value">{formatUsdTotal(agentHealth.costUsdTotal)}</span>
            <span className="rc-stat-tile__sub">
              {formatUsdTotal(agentHealth.costUsd24h)} in 24h
            </span>
          </div>
          <StatTile
            label="Users total"
            value={formatStatValue(now.usersTotal)}
            onOpen={drill("signups", "all", "Users")}
          />
          <StatTile
            label="Projects total"
            value={formatStatValue(now.projectsTotal)}
            onOpen={drill("new_projects", "all", "Projects")}
          />
        </div>
        <LiveFunnel live={liveFunnel} />
      </section>

      <h2 className="lp-section-title">Last 24 hours</h2>
      <section className="lp-charts" aria-label="Last 24 hours by hour">
        <BarChart
          title="Client messages"
          buckets={hourly.hours}
          series={[chartSeries("messages", "Client messages", hourly.userMessages)]}
          colors={seriesColors}
          windowLabel="in 24h"
          onOpen={drill("client_messages", "24h", "Client messages")}
        />
        <BarChart
          title="New signups"
          buckets={hourly.hours}
          series={[chartSeries("signups", "New signups", hourly.newUsers)]}
          colors={seriesColors}
          windowLabel="in 24h"
          onOpen={drill("signups", "24h", "New signups")}
        />
        <BarChart
          title="New projects"
          buckets={hourly.hours}
          series={[chartSeries("projects", "New projects", hourly.newProjects)]}
          colors={seriesColors}
          windowLabel="in 24h"
          onOpen={drill("new_projects", "24h", "New projects")}
        />
        <BarChart
          title="Searches"
          buckets={hourly.hours}
          series={[chartSeries("searches", "Searches", hourly.searchRuns)]}
          colors={seriesColors}
          windowLabel="in 24h"
          onOpen={drill("searches", "24h", "Searches")}
        />
        {/* Bucketed on the talent's own inbox event time, so a stalled push lane
            thins out the recent bars instead of hiding and then drawing one fake
            spike when the 30-minute sweep backfills. */}
        <BarChart
          title="Talent replies"
          buckets={hourly.hours}
          series={[chartSeries("replies", "Talent replies", hourly.sellerReplies)]}
          colors={seriesColors}
          windowLabel="in 24h"
        />
        <BarChart
          title="Talent picked"
          buckets={hourly.hours}
          series={[chartSeries("picks", "Talent picked", hourly.picks)]}
          colors={seriesColors}
          windowLabel="in 24h"
          onOpen={drill("picks", "24h", "Talent picked")}
        />
        <BarChart
          title="Agent errors"
          buckets={hourly.hours}
          series={[chartSeries("errors", "Agent errors", hourly.llmErrors)]}
          colors={seriesColors}
          windowLabel="in 24h"
          onOpen={drill("errors", "24h", "Agent errors")}
        />
        {/* Frustration over the day. A bar is the CLIENTS who sounded
            frustrated in that hour, so it reads in the same unit as the tile
            above; the caption is the backend's 24h distinct count rather than
            the sum of the bars, which would count a client once per bad hour.
            Last in the row on purpose: it is the one chart here whose bars
            going UP is bad news, so it sits beside agent errors rather than
            among the growth series. */}
        <BarChart
          title="Unsatisfied clients"
          buckets={hourly.hours}
          series={[chartSeries("frustration", "Unsatisfied clients", hourly.unsatisfied)]}
          colors={seriesColors}
          windowLabel="in 24h"
          total={now.unsatisfiedUsers24h}
          onOpen={drill("unsatisfied", "24h", "Unsatisfied clients")}
        />
      </section>

      {/* Agent errors, demoted on request: the error-rate gauge up top carries
          the health signal, so the minute detail rides small at the very
          bottom for whoever wants the shape. */}
      <div className="lp-bottom">
        <LineAreaChart
          title="Agent errors, minute by minute"
          buckets={minutely.minutes}
          series={[chartSeries("errors", "Agent errors", minutely.llmErrors)]}
          colors={seriesColors}
          totalLabel={`${formatStatValue(minutely.llmErrors.reduce((a, b) => a + b, 0))} in the last hour`}
          compact
          onOpen={drill("errors", "1h", "Agent errors")}
        />
      </div>
    </>
  );
}

// --- Page shell -------------------------------------------------------------

/** Inert: only ever handed to a DISABLED query, whose fn never runs. */
const PLACEHOLDER_SCOPE = { experiment: "", arm: "control" } as const;

export function LaunchPage({
  session,
  onSessionEnded,
  scope,
  onScopeChange,
}: {
  session?: LaunchSession;
  /** The A/B group this board is read for, or undefined for everyone. Absent on
   *  the KIOSK: the scoped read is employee-gated, and a shared screen in a room
   *  has no business asking for one. */
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
  /** Kiosk only: the remote-view token was rejected, so it is dead for good.
   *  Reported UP to `LaunchKiosk`, which owns the credential and can put the
   *  code form back; without it this page would sit on a dead error screen. */
  onSessionEnded?: () => void;
}) {
  const kiosk = Boolean(session);
  // Two lanes, exactly one enabled. The whole-population board is a pure cache
  // read the TV polls; a scoped one is built on demand and answers `computing`
  // while the worker runs it, so they cannot share a query.
  const scoped = !kiosk && scope !== undefined;
  const whole = useQuery({ ...launchDashboardQuery(session?.token), enabled: !scoped });
  const armed = useQuery({
    // A disabled query never calls its fn, so the placeholder is inert; it
    // exists only to satisfy the signature on the branch that is switched off.
    ...launchArmQuery(scope ?? PLACEHOLDER_SCOPE),
    enabled: scoped,
  });
  const dashboard = scoped ? armed : whole;
  const armStatus = scoped ? armed.data?.status : undefined;
  const boardData = scoped ? (armed.data?.dashboard ?? undefined) : whole.data;
  // Area lookup only matters for showing the mint button; the kiosk never asks.
  const areas = useQuery({ ...myAreasQuery, enabled: !kiosk });
  // FULL access: minting a remote-view code hands a TV a live read of this
  // dashboard, and the route takes `system_admin` at `full`. A read-only System
  // Admin reviews the console's roster and mints nothing.
  const canMintRemote = !kiosk && canWrite(areas.data ?? [], "system_admin");

  const [remoteOpen, setRemoteOpen] = useState(false);
  // The open drilldown, or null. Stays null forever on the kiosk: the tiles
  // render unclickable there, because the remote-view token buys the aggregate
  // read only and the drilldown names users and projects.
  const [drill, setDrill] = useState<LaunchDrill | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { theme, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    // `?theme=dark` is the TYPABLE theme switch for the TV (no sidebar, so no
    // toggle to click before this button existed). applyTheme persists it, so
    // it survives reloads without keeping the param in the URL.
    const requested = themeFromSearch(window.location.search);
    if (requested) applyTheme(requested);
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void rootRef.current?.requestFullscreen();
    }
  };

  // The 15s timer has switched itself off because the credential behind it is
  // dead (`launchRefetchInterval`), on either path in. Drives the header
  // subline, so the board never claims to be live while showing frozen numbers.
  const pollStopped = isAuthFailure(dashboard.error);

  // 401 counts as a rejected TOKEN, not just 403. An expired remote-view token
  // falls THROUGH the backend's token check to the ordinary employee gate
  // (`require_launch_viewer`), and a TV carries no employee cookie, so the
  // honest answer on the exact path that matters is 401. Matching only 403 left
  // the kiosk holding a token it never dropped, refetching it every 15 seconds.
  const tokenRejected =
    kiosk &&
    dashboard.error instanceof ApiError &&
    (dashboard.error.isForbidden || dashboard.error.isUnauthorized);

  useEffect(() => {
    // A rejected token is dead for good (expired or revoked): drop it so the
    // next reload lands on the code form instead of this error again, and tell
    // the kiosk so this load does too.
    if (!tokenRejected) return;
    clearLaunchSession();
    onSessionEnded?.();
  }, [tokenRejected, onSessionEnded]);

  return (
    <div className={kiosk ? "lp lp--kiosk" : "lp"} ref={rootRef}>
      <header className="lp-head">
        <div className="lp-head__id">
          <img className="lp-head__art" src={launchArt} alt="" role="presentation" />
          <div>
            <h1 className="lp-head__title">Mira launch day</h1>
            {/* Say which of the two it is. Leaving "Live, refreshes every N
                seconds" up after the poll stopped would be a screen claiming to
                be current while showing frozen numbers, which is worse than no
                board at all. */}
            {pollStopped ? (
              <span className="lp-head__updated">
                Paused. This session expired, so the board stopped refreshing.
              </span>
            ) : boardData ? (
              <span className="lp-head__updated">
                <span className="lp-live" aria-hidden="true" />
                Live, updated{" "}
                {new Date(boardData.generatedAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
                , refreshes every {Math.round(LAUNCH_POLL_MS / 60_000)} minutes
              </span>
            ) : null}
          </div>
        </div>
        <div className="lp-head__tools">
          {dashboard.isFetching && boardData ? (
            <span className="lp-head__spinner" role="status" aria-label="Refreshing">
              <Spinner />
            </span>
          ) : null}
          {!kiosk && onScopeChange ? (
            <ExperimentFilter scope={scope} onChange={onScopeChange} />
          ) : null}
          <Clock />
          <IconButton
            variant="ghost"
            tone="neutral"
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            title={theme === "dark" ? "Light theme" : "Dark theme"}
            onClick={toggleTheme}
          >
            {theme === "dark" ? <SunIcon size={18} /> : <MoonIcon size={18} />}
          </IconButton>
          {canMintRemote ? (
            <Button variant="outline" tone="neutral" onClick={() => setRemoteOpen(true)}>
              Open in remote
            </Button>
          ) : null}
          <Button variant="outline" tone="neutral" onClick={toggleFullscreen}>
            {isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          </Button>
        </div>
      </header>

      {armStatus === "computing" ? (
        <p className="lp-notice">
          Measuring this group. It runs off the request, so give it a few seconds, this page picks
          it up on its own.
        </p>
      ) : null}
      {armStatus === "unavailable" ? (
        <p className="lp-notice">
          Nobody has been sorted into this group yet, so there is nothing to filter by. The split is
          published every half hour; try again shortly.
        </p>
      ) : null}
      {/* Say which numbers the filter did NOT reach. Spend and blocked accounts
          measure the PLATFORM, not a cohort, so they stay whole-population, and a
          reader who cannot see that reads every tile as filtered. */}
      {boardData?.armScope ? (
        <p className="lp-notice">
          Spend, model calls, errors and blocked accounts are platform totals and are not split by
          group. Every other number here is this group's.
        </p>
      ) : null}

      {dashboard.isPending && !boardData ? <LoadingState label="Loading the numbers…" /> : null}
      {dashboard.isError ? (
        tokenRejected ? (
          <ErrorState
            error={
              new ApiError(
                403,
                "This remote link has expired. Ask an operator to mint a fresh code on /launch.",
              )
            }
          />
        ) : (
          <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
        )
      ) : null}
      {boardData ? <Dashboard data={boardData} onDrill={kiosk ? undefined : setDrill} /> : null}

      {drill ? <LaunchDrilldownModal drill={drill} onClose={() => setDrill(null)} /> : null}
      {remoteOpen ? <RemoteLinkModal onClose={() => setRemoteOpen(false)} /> : null}
    </div>
  );
}
